import { Controller } from "@hotwired/stimulus"

// Thin wrapper over the native <dialog>.
//
// showModal() gives focus trapping, Escape-to-close, inertness of the rest of
// the page and the browser's top layer for free. None of that is worth
// reimplementing, and hand-rolled versions get the focus handling wrong.
//
// Two shapes, because the panel needed the second one:
//
// 1. On an ancestor of the dialog, with `data-dialog-target="dialog"` on the
//    <dialog> and `data-action="dialog#open"` on a button beside it. This is
//    the board's filter overlay.
// 2. On the <dialog> itself, with `data-dialog-opener-value` naming the id of
//    the control that opens it. The panel shell owns the header row where a
//    page's actions live, so an employer screen cannot wrap that button and
//    its dialog in one element; the dialog reaches out for its opener instead.
//    Same element, same showModal(), one mechanism.
export default class extends Controller {
  static targets = ["dialog", "field"]
  static values = { opener: String, stashKey: String, failed: Boolean }

  connect() {
    if (this.hasOpenerValue) {
      this.opener = document.getElementById(this.openerValue)
      if (this.opener) {
        this.openFromOpener = this.openFromOpener.bind(this)
        this.opener.addEventListener("click", this.openFromOpener)
      }
    }

    this.restore()
  }

  disconnect() {
    if (this.opener) this.opener.removeEventListener("click", this.openFromOpener)
  }

  // The opener is a real link to a real fallback target, so opening the dialog
  // means not following it.
  openFromOpener(event) {
    event.preventDefault()
    this.open()
  }

  open() {
    const dialog = this.dialogElement
    if (!dialog) return

    dialog.showModal()
    // Belt and braces over `autofocus`: the first field, never the close
    // button that happens to come first in the source order.
    if (this.hasFieldTarget) this.fieldTarget.focus()
  }

  close() {
    this.dialogElement?.close()
  }

  // Keep what was typed across the redirect the form's controller performs.
  // Session storage rather than a hidden round trip, because the server's
  // contract here is a redirect with a flash and it is not ours to change.
  stash() {
    if (!this.hasStashKeyValue || !this.hasFieldTarget) return

    try {
      window.sessionStorage.setItem(this.stashKeyValue, this.fieldTarget.value)
    } catch (error) {
      // A browser with storage switched off loses the draft and nothing else.
    }
  }

  // Put it back, and reopen only when the page we came back to is reporting a
  // failure. A stash with no failure is a successful invitation, so the value
  // is dropped and the dialog stays shut.
  restore() {
    if (!this.hasStashKeyValue || !this.hasFieldTarget) return

    let stashed = null
    try {
      stashed = window.sessionStorage.getItem(this.stashKeyValue)
      window.sessionStorage.removeItem(this.stashKeyValue)
    } catch (error) {
      return
    }

    if (!stashed) return

    this.fieldTarget.value = stashed
    if (!this.failedValue) return

    this.open()
    this.fieldTarget.select()
  }

  // Clicking the backdrop closes. The dialog element itself fills the backdrop
  // area, so the check is whether the click landed outside its content box.
  backdropClose(event) {
    const dialog = this.dialogElement
    if (!dialog || event.target !== dialog) return

    const box = dialog.getBoundingClientRect()
    const inside =
      event.clientX >= box.left && event.clientX <= box.right &&
      event.clientY >= box.top && event.clientY <= box.bottom

    if (!inside) dialog.close()
  }

  // The dialog is either a target inside this controller's element, or this
  // controller's element is the dialog.
  get dialogElement() {
    if (this.hasDialogTarget) return this.dialogTarget

    return this.element.tagName === "DIALOG" ? this.element : null
  }
}
